import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Pencil, Trash2, Building2, Loader2 } from 'lucide-react'
import { useClients, useDeleteClient } from '@/hooks/queries/useClients'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { formatRut } from '@/lib/utils'
import { ClientsToolbar } from '@/pages/clients/components/ClientsToolbar'
import type { Client } from '@/types'

export function ClientsListPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<Client | null>(null)

  const { data: clients = [], isLoading, isError } = useClients()
  const deleteMutation = useDeleteClient()
  const clearFilters = () => setSearch('')
  const hasActiveFilters = Boolean(search.trim())

  const filtered = useMemo(() => {
    const term = search.toLowerCase()
    if (!term) return clients
    return clients.filter(
      (c) =>
        c.trade_name.toLowerCase().includes(term) ||
        c.business_name.toLowerCase().includes(term) ||
        c.rut.toLowerCase().includes(term)
    )
  }, [clients, search])

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteMutation.mutateAsync(deleteTarget.id)
      setDeleteTarget(null)
    } catch {
      // Error manejado por React Query
    }
  }

  return (
    <div className="space-y-6">
      {/* Encabezado */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Clientes</h1>
          <p className="text-sm text-muted-foreground">
            Administra las empresas y organizaciones que utilizan el servicio
          </p>
        </div>
        <Button className="w-full sm:w-auto" onClick={() => navigate('/clients/new')}>
          <Plus className="mr-2 h-4 w-4" />
          Nuevo cliente
        </Button>
      </div>

      <Card>
        <CardHeader className="space-y-4">
          <ClientsToolbar
            search={search}
            onSearchChange={setSearch}
            onClearFilters={clearFilters}
            hasActiveFilters={hasActiveFilters}
          />
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#7c8c43]" />
            </div>
          ) : isError ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              Error al cargar los clientes. Por favor recarga la página.
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Building2 className="mb-4 h-12 w-12 text-muted-foreground/40" />
              <p className="text-lg font-medium text-muted-foreground">
                {search ? 'No se encontraron clientes' : 'No hay clientes aún'}
              </p>
              <p className="mt-1 text-sm text-muted-foreground/70">
                {search
                  ? 'Intenta con otro término de búsqueda'
                  : 'Crea el primer cliente haciendo clic en "Nuevo cliente"'}
              </p>
            </div>
          ) : (
            <>
              {/* Cards para móvil */}
              <div className="space-y-3 md:hidden">
                {filtered.map((client) => (
                  <Card key={client.id}>
                    <CardContent className="space-y-3 p-4">
                      <div className="flex items-start gap-3">
                        {client.logo_path ? (
                          <img
                            src={client.logo_path}
                            alt={client.trade_name}
                            className="h-10 w-10 rounded object-contain"
                          />
                        ) : (
                          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded bg-primary/20">
                            <Building2 className="h-5 w-5 text-[#7c8c43]" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-foreground">{client.trade_name}</p>
                          <p className="text-xs text-muted-foreground">{client.business_name}</p>
                          <Badge variant="outline" className="mt-1">
                            {formatRut(client.rut)}
                          </Badge>
                        </div>
                      </div>
                      {(client.city || client.industry_type) && (
                        <p className="text-xs text-muted-foreground">
                          {[client.city, client.industry_type].filter(Boolean).join(' · ')}
                        </p>
                      )}
                      {client.contact_name && (
                        <p className="text-sm text-foreground/70">{client.contact_name}</p>
                      )}
                      {client.waste_subcategories.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {client.waste_subcategories.map((ws) => (
                            <Badge key={ws.id} variant="secondary" className="text-xs">
                              {ws.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          className="flex-1"
                          onClick={() => navigate(`/clients/${client.id}/edit`)}
                        >
                          <Pencil className="mr-1 h-4 w-4" />
                          Editar
                        </Button>
                        <Button
                          variant="ghost"
                          className="flex-1 text-red-500 hover:bg-red-50 hover:text-red-600"
                          onClick={() => setDeleteTarget(client)}
                        >
                          <Trash2 className="mr-1 h-4 w-4" />
                          Eliminar
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Tabla para escritorio */}
              <div className="hidden overflow-hidden rounded-lg border bg-white md:block">
                <table className="w-full text-sm">
                  <thead className="border-b bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                        Nombre comercial
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                        RUT
                      </th>
                      <th className="hidden px-4 py-3 text-left font-medium text-muted-foreground lg:table-cell">
                        Contacto
                      </th>
                      <th className="hidden px-4 py-3 text-left font-medium text-muted-foreground xl:table-cell">
                        Tipos de residuo
                      </th>
                      <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filtered.map((client) => (
                      <tr
                        key={client.id}
                        className="transition-colors hover:bg-muted/40"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {client.logo_path ? (
                              <img
                                src={client.logo_path}
                                alt={client.trade_name}
                                className="h-8 w-8 rounded object-contain"
                              />
                            ) : (
                              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded bg-primary/20">
                                <Building2 className="h-4 w-4 text-[#7c8c43]" />
                              </div>
                            )}
                            <div>
                              <p className="font-medium text-foreground">
                                {client.trade_name}
                              </p>
                              <p className="text-xs text-muted-foreground/70">
                                {client.business_name}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline">{formatRut(client.rut)}</Badge>
                        </td>
                        <td className="hidden px-4 py-3 text-foreground/70 lg:table-cell">
                          <div>
                            {client.contact_name && (
                              <p>{client.contact_name}</p>
                            )}
                            {client.email && (
                              <p className="text-xs text-muted-foreground/70">{client.email}</p>
                            )}
                            {(client.city || client.industry_type) && (
                              <p className="text-xs text-muted-foreground/70">
                                {[client.city, client.industry_type].filter(Boolean).join(' · ')}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="hidden px-4 py-3 xl:table-cell">
                          <div className="flex flex-wrap gap-1">
                            {client.waste_subcategories.map((ws) => (
                              <Badge key={ws.id} variant="secondary" className="text-xs">
                                {ws.name}
                              </Badge>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => navigate(`/clients/${client.id}/edit`)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-500 hover:bg-red-50 hover:text-red-600"
                              onClick={() => setDeleteTarget(client)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {clients.length > 0 && (
                <p className="text-sm text-muted-foreground">
                  {filtered.length === clients.length
                    ? `${clients.length} cliente${clients.length !== 1 ? 's' : ''}`
                    : `${filtered.length} de ${clients.length} clientes`}
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Diálogo de confirmación de eliminación */}
      <Dialog open={Boolean(deleteTarget)} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar cliente</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de que deseas eliminar a{' '}
              <strong>{deleteTarget?.trade_name}</strong>? Esta acción no se
              puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              disabled={deleteMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Eliminando...
                </>
              ) : (
                'Eliminar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
